import { defineComponent as _defineComponent } from 'vue'

const __default__ = {
  inheritAttrs: false
}

import { ref, computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import ZIcon from '~/components/zeal/ZIcon/ZIcon.vue'
import { debounce } from '~/utils/debounce'

interface Option {
  label: string
  value: string
}

interface InternalOption extends Option {
  lowerLabel: string
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'UICombobox',
  props: {
  label: {
    type: String,
    default: ''
  },
  modelValue: {
    type: String,
    default: ''
  },
  options: {
    type: Array as () => (string | Option)[],
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select an option...'
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  searchable: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  },
  helperText: {
    type: String,
    default: ''
  },
  disableFiltering: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
},
  emits: ['update:modelValue', 'change', 'search'],
  setup(__props, { emit }) {

const props = __props;





const isOpen = ref(false)
const searchQuery = ref('')
const container = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const triggerButton = ref<HTMLElement | null>(null)
const activeIndex = ref(-1)

// Generate unique IDs (client-side only to avoid SSR hydration mismatch)
const uniqueId = ref('')
const inputId = computed(() => (uniqueId.value ? `combobox-input-${uniqueId.value}` : undefined))
const listboxId = computed(() =>
  uniqueId.value ? `combobox-listbox-${uniqueId.value}` : undefined
)

onMounted(() => {
  uniqueId.value = Math.random().toString(36).substring(2, 11)
})

const normalizedOptions = computed<InternalOption[]>(() => {
  return props.options.map((opt) => {
    let option: Option
    if (typeof opt === 'object' && opt !== null && 'value' in opt) {
      option = opt as Option
    } else {
      option = { label: String(opt), value: String(opt) }
    }
    return {
      ...option,
      lowerLabel: option.label.toLowerCase()
    }
  })
})

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value || props.disableFiltering) {
    return normalizedOptions.value
  }
  const query = searchQuery.value.toLowerCase()
  return normalizedOptions.value.filter((opt) => opt.lowerLabel.includes(query))
})

const selectedOption = computed(() => {
  return normalizedOptions.value.find((opt) => {
    // Handle both number and string comparison
    return opt.value === props.modelValue || String(opt.value) === String(props.modelValue)
  })
})

const displayValue = computed(() => {
  if (selectedOption.value) {
    return selectedOption.value.label
  }
  return props.placeholder
})

const hasValue = computed(() => {
  return props.modelValue !== '' && props.modelValue !== null && props.modelValue !== undefined
})

const activeOptionId = computed(() => {
  if (isOpen.value && activeIndex.value >= 0 && activeIndex.value < filteredOptions.value.length) {
    return getOptionId(activeIndex.value)
  }
  return undefined
})

const isSelected = (option: Option) => {
  // Handle both number and string comparison
  return props.modelValue === option.value || String(props.modelValue) === String(option.value)
}

const getOptionId = (index: number) => {
  return uniqueId.value ? `combobox-option-${uniqueId.value}-${index}` : undefined
}

const toggle = () => {
  if (props.disabled) return
  if (isOpen.value) {
    close()
  } else {
    open()
  }
}

const open = () => {
  isOpen.value = true
  searchQuery.value = ''
  activeIndex.value = filteredOptions.value.findIndex(
    (opt) => opt.value === props.modelValue || String(opt.value) === String(props.modelValue)
  )
  if (activeIndex.value === -1 && filteredOptions.value.length > 0) {
    activeIndex.value = 0
  }

  if (props.searchable) {
    nextTick(() => {
      searchInput.value?.focus()
    })
  }
  document.addEventListener('click', handleClickOutside)
}

const close = () => {
  isOpen.value = false
  activeIndex.value = -1
  document.removeEventListener('click', handleClickOutside)
  // return focus to trigger if needed
  if (!props.searchable) {
    triggerButton.value?.focus()
  }
}

const selectOption = (option: Option) => {
  emit('update:modelValue', option.value)
  emit('change', option.value)
  close()
  triggerButton.value?.focus()
}

const focusTrigger = () => {
  triggerButton.value?.focus()
}

// Navigation Logic
const navigate = (direction: 'up' | 'down') => {
  if (!isOpen.value) {
    open()
    return
  }

  if (filteredOptions.value.length === 0) return

  if (direction === 'down') {
    activeIndex.value =
      activeIndex.value === filteredOptions.value.length - 1 ? 0 : activeIndex.value + 1
  } else {
    activeIndex.value =
      activeIndex.value <= 0 ? filteredOptions.value.length - 1 : activeIndex.value - 1
  }
}

const handleEnter = () => {
  if (isOpen.value && activeIndex.value >= 0 && activeIndex.value < filteredOptions.value.length) {
    selectOption(filteredOptions.value[activeIndex.value])
  } else if (!isOpen.value) {
    open()
  } else {
    close()
  }
}

const handleCommonKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      navigate('down')
      return true
    case 'ArrowUp':
      event.preventDefault()
      navigate('up')
      return true
    case 'Enter':
      event.preventDefault()
      handleEnter()
      return true
    case 'Escape':
      event.preventDefault()
      if (isOpen.value) {
        close()
        event.stopPropagation()
        triggerButton.value?.focus()
      }
      return true
    default:
      break
  }
  return false
}

const handleTriggerKeydown = (event: KeyboardEvent) => {
  if (props.disabled) return

  if (handleCommonKeydown(event)) return

  // Trigger-specific keys
  if (event.key === ' ') {
    event.preventDefault()
    handleEnter()
  }
}

const handleSearchKeydown = (event: KeyboardEvent) => {
  // Search-specific: Don't prevent default on Space (typing)
  // But Common handlers don't include Space, so we are good.
  handleCommonKeydown(event)
}

const handleClickOutside = (event: MouseEvent) => {
  if (container.value && !container.value.contains(event.target as Node)) {
    close()
  }
}

// Watch filters to reset active index if needed
watch(filteredOptions, (newOptions) => {
  if (!isOpen.value) return
  const active = newOptions[activeIndex.value]
  if (active) return // still in-bounds
  // try re-anchor by current activeOptionId/value
  const selectedIndex = newOptions.findIndex(
    (opt) => opt.value === props.modelValue || String(opt.value) === String(props.modelValue)
  )
  activeIndex.value = selectedIndex !== -1 ? selectedIndex : newOptions.length ? 0 : -1
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

const debouncedSearch = debounce((newQuery: string) => {
  emit('search', newQuery)
})

const handleSearchInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  searchQuery.value = target.value
  debouncedSearch(target.value)
}

return { __sfc: true,props, emit, isOpen, searchQuery, container, searchInput, triggerButton, activeIndex, uniqueId, inputId, listboxId, normalizedOptions, filteredOptions, selectedOption, displayValue, hasValue, activeOptionId, isSelected, getOptionId, toggle, open, close, selectOption, focusTrigger, navigate, handleEnter, handleCommonKeydown, handleTriggerKeydown, handleSearchKeydown, handleClickOutside, debouncedSearch, handleSearchInput, ZIcon }
}

})